import axios from 'axios';
import { api } from '../utils/api';

// API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5004/api';

// Email provider types
export type EmailProvider = 'sendgrid' | 'gmail';

// Email status interface
export interface EmailStatus {
  connected: boolean;
  email?: string;
  message: string;
  error?: string;
}

// Email statistics interface
export interface EmailStatistics {
  total_emails: number;
  opened_emails: number;
  responded_emails: number;
  open_rate: number;
  response_rate: number;
  top_professors: {
    id: number;
    name: string;
    email: string;
    university: string | null;
    department: string | null;
    response_score: number;
    emails_sent: number;
  }[];
}

/**
 * Send an email to a professor
 * @param professorId - ID of the professor
 * @param subject - Email subject
 * @param content - HTML content of the email
 * @param userId - Optional user ID
 * @param provider - Email provider to use ('sendgrid' or 'gmail')
 * @returns Promise with the response data
 */
export const sendEmail = async (
  professorId: number,
  subject: string,
  content: string,
  userId?: number,
  provider: EmailProvider = 'sendgrid',
  recipientEmail?: string
): Promise<any> => {
  try {
    const response = await api.post(`/send-email`, {
      professor_id: professorId,
      subject,
      content,
      user_id: userId,
      provider,
      recipient_email: recipientEmail
    });
    return response.data;
  } catch (error: any) {
    console.error('Error sending email:', error);
    
    // Check if error is due to Gmail authentication required
    if (error.response?.status === 401 && error.response?.data?.auth_url) {
      // Redirect to Gmail authentication
      window.location.href = error.response.data.auth_url;
    }
    
    throw error;
  }
};

/**
 * Track a response from a professor
 * @param professorId - ID of the professor
 * @param responseReceived - Whether a response was received
 * @returns Promise with the response data
 */
export const trackResponse = async (
  professorId: number,
  responseReceived: boolean = true
): Promise<any> => {
  try {
    const response = await axios.post(`${API_URL}/track-response`, {
      professor_id: professorId,
      response_received: responseReceived
    });
    return response.data;
  } catch (error) {
    console.error('Error tracking response:', error);
    throw error;
  }
};

/**
 * Check Gmail connection status
 * @returns Promise with the Gmail status
 */
export const checkGmailStatus = async (): Promise<EmailStatus> => {
  try {
    // Use the API instance which already has withCredentials set to true
    const response = await api.get('/gmail/status');
    console.log('Gmail status response:', response.data);
    return response.data;
  } catch (error: any) {
    console.warn('Gmail status check failed (email service not configured):', error.message);
    // Return disconnected status instead of throwing - email service is optional
    return {
      connected: false,
      message: 'Email service not configured'
    };
  }
};

/**
 * Disconnect Gmail
 * @returns Promise with the response data
 */
export const disconnectGmail = async (): Promise<any> => {
  try {
    // Try GET request first, which might be more reliable with certain CORS configurations
    const response = await api.get('/gmail/disconnect');
    console.log('Gmail disconnect response:', response.data);
    return response.data;
  } catch (error) {
    // Fall back to POST if GET fails
    try {
      const response = await api.post('/gmail/disconnect');
      console.log('Gmail disconnect response (POST):', response.data);
      return response.data;
    } catch (postError) {
      console.error('Error disconnecting Gmail:', postError);
      throw postError;
    }
  }
};

/**
 * Get email statistics
 * @returns Promise with the email statistics
 */
export const getEmailStatistics = async (): Promise<EmailStatistics> => {
  try {
    const response = await axios.get(`${API_URL}/email-stats`);
    return response.data;
  } catch (error) {
    console.error('Error getting email statistics:', error);
    throw error;
  }
};

/**
 * Generate a personalized email for a professor
 * @param professorId - ID of the professor
 * @param userInfo - User information
 * @param customNotes - Additional notes to include
 * @returns Promise with the generated email
 */
export const generatePersonalizedEmail = async (
  professorId: number,
  userInfo: {
    name?: string;
    school?: string;
    grade?: string;
    interests?: string[];
    achievements?: string[];
  },
  customNotes?: string,
  professorName?: string,
  professorResearch?: string
): Promise<{ subject: string; content: string; professor_email: string }> => {
  try {
    console.log('Calling API with:', {
      professorId,
      userInfo,
      customNotes,
      professorName,
      professorResearch
    });
    
    const response = await api.post(`/generate-personalized-email`, {
      professor_id: professorId,
      user_info: userInfo,
      custom_notes: customNotes,
      professor_name: professorName,
      professor_research: professorResearch
    });
    return response.data;
  } catch (error) {
    console.error('Error generating personalized email:', error);
    throw error;
  }
};
